import { FC, useContext, useEffect } from "react";
import Select, { GroupBase, OptionsOrGroups, PropsValue } from "react-select";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

import HubInputField from "src/components/inputType";

import useNPILookup from "src/hooks/useNPILookup";
import useFetch from "src/hooks/useFetch";
import useAddNewProvider from "src/hooks/useAddNewProvider";

import { AddProviderSchema } from "src/validation/addProvider";

import { AddProviderModalProps } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import { AddProviderForm, NPISpeciality } from "src/pages/addProvider/types";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";
import { AuthContext } from "src/context/authContext";



const AddProviderModal: FC<AddProviderModalProps> = ({ isModalVisible, title, iAm, practice_id, hideModal, updateModalDoctor }): JSX.Element => {
  const {
    control, formState, reset: resetForm,
    handleSubmit: handleDoctorForm, getValues, setValue, trigger,
  } = useForm<AddProviderForm>({
    mode: "all",
    resolver: yupResolver(AddProviderSchema(title)),
    defaultValues: {
      practice_id: practice_id || 0,
    }
  });
  const { userData } = useContext(AuthContext)

  const { callNPILookup } = useNPILookup(setValue, trigger)
  const { data: specialityData } = useFetch<NPISpeciality[]>(APIEndpoints.GET_PROVIDER_SPECIALITIES);
  const { data: practiceListing } =
    useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, (practice_id ? { practice_id } : { search_query: "" }))


  const { addProviderLoading, addProviderResponse, addNewProvider } = 
    useAddNewProvider(title, resetForm, specialityData)


  useEffect(() => {
    if (addProviderResponse) {
      const { status: { global_doctor, message } } = addProviderResponse
      const { firstname, lastname } = getValues()
      updateModalDoctor({ value: global_doctor, label: `${firstname} ${lastname}` }, !message.includes("already exists"))

      resetForm()
      hideModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addProviderResponse])

  useEffect(() => {
    if (practice_id)
      setValue("practice_id", practice_id)
    else if (practiceListing && practiceListing.data && practiceListing.data.length > 0)
      setValue("practice_id", practiceListing.data[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceListing])



  return (
    <div
      className={`modal fade ${isModalVisible ? 'show show-modal' : ''}`}
      id="addProviderModal"
      tabIndex={-1}
      data-bs-backdrop="true"
      aria-hidden="true"
      onClick={hideModal}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="addProviderModalLabel">
              {title}
            </h3>
            <button type="button" className="btn-close text-light" aria-label="Close" onClick={hideModal}></button>
          </div>

          <div className="modal-body">
            <form>
              <div className="row mb-3">
                <label htmlFor="practice_id" className="col-md-2 col-12 form-label">Practice *</label>
                <div className="col-md-10 col-12">
                  <Controller
                    name="practice_id"
                    control={control}
                    defaultValue={practice_id}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          options={(
                            practiceListing && practiceListing.data && practiceListing.data.length > 0 ?
                              practiceListing.data.map((prac) => ({
                                  value: prac.id,
                                  label: `${prac.name}${prac.address ? ` - ${prac.address.full_address}` : ""}`
                                }))
                                : []
                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>}
                          isSearchable={true}
                          id="practice_id"
                          placeholder="Enter Practice Name..."
                          isDisabled={
                            (practice_id > 0) || 
                            (
                              ["Practice", "Contact"].includes(userData?.ref_type || "") &&
                              ["Treating Provider"].includes(iAm)
                            )
                          }
                          required
                          {...field}
                          value={(() => {
                            if (practiceListing && practiceListing.data && practiceListing.data.length > 0) {
                              let practice_data;
                              if (practice_id)
                                practice_data = practiceListing.data.find((prac) => prac.id === practice_id)
                              else
                                practice_data = practiceListing.data[0]
                              if (practice_data) {
                                return {
                                  value: practice_data.id,
                                  label: `${practice_data.name}${practice_data.address ? ` - ${practice_data.address.full_address}` : ""}`
                                } as unknown as PropsValue<string>
                              } else 
                                return undefined
                            } else 
                              return undefined
                          })()}
                        />
                        {error && error.message ? (
                          <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2"></div>
                        ) : null}
                      </>
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6 col-12">
                  <Controller
                    name="npi"
                    control={control}
                    defaultValue=""
                    render={( { field, fieldState: { error } } ) => (
                      <div className={"row"}>
                        <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>NPI *</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="npi"
                            isRowBased={false}
                            isRequired={true}
                            field={field}
                            error={error}
                            inputPlaceholder="NPI"
                            onBlur={() => callNPILookup(formState.errors, getValues)}
                            onKeyDown={( event ) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                callNPILookup(formState.errors, getValues)
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="ref_taxonomy_id"
                    control={control}
                    defaultValue=""
                    render={( { field, fieldState: { error } } ) => (
                      <div className="row">
                        <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>Spec. *</label>

                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="ref_taxonomy_id"
                            isRowBased={false}
                            isRequired={true}
                            field={field}
                            error={error}
                            // inputLabel="Spec."
                            inputPlaceholder="Select a Taxonomy..."
                            isDropdown={true}
                            isSearchable={true}
                            dropdownData={
                              specialityData ?
                                specialityData.map(
                                  ( speciality ) => ( {
                                    label: speciality.name,
                                    value: speciality.code
                                  } )
                                ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                                : []
                            }
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="firstname"
                    control={control}
                    defaultValue=""
                    render={( { field, fieldState: { error } } ) => (
                      <div className={"row"}>
                        <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>First
                          Name *</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="firstname"
                            isRowBased={false}
                            isRequired={true}
                            field={field}
                            error={error}
                            // inputLabel="First Name"
                            inputPlaceholder="First Name"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="lastname"
                    control={control}
                    defaultValue=""
                    render={( { field, fieldState: { error } } ) => (
                      <div className={"row"}>
                        <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>Last
                          Name *</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="lastname"
                            isRowBased={false}
                            isRequired={true}
                            field={field}
                            error={error}
                            // inputLabel="Last Name"
                            inputPlaceholder="Last Name"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="dea"
                    control={control}
                    defaultValue=""
                    render={( { field, fieldState: { error } } ) => (
                      <div className={"row"}>
                        <label
                          className={"form-label col-lg-4 col-md-4 col-12 p-2"}>DEA</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="dea"
                            isRowBased={false}
                            isRequired={false}
                            field={field}
                            error={error}
                            // inputLabel="DEA"
                            inputPlaceholder="DEA Number"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="sln"
                    control={control}
                    defaultValue=""
                    render={( { field, fieldState: { error } } ) => (
                      <div className={"row"}>
                        <label
                          className={"form-label col-lg-4 col-md-4 col-12 p-2"}>SLN</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="sln"
                            isRowBased={false}
                            isRequired={false}
                            field={field}
                            error={error}
                            // inputLabel="SLN"
                            inputPlaceholder="State License Number"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="tax_id"
                    control={control}
                    defaultValue=""
                    render={( {
                      field,
                      fieldState: { error }
                    } ) => (
                      <div className={"row"}>
                        <label
                          className={"form-label col-lg-4 col-md-4 col-12 p-2"}>Tax
                          ID</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="tax_id"
                            isRowBased={false}
                            isRequired={false}
                            field={field}
                            error={error}
                            // inputLabel="Tax ID"
                            inputPlaceholder="Tax ID"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-6 col-12">
                  <Controller
                    name="ptan"
                    control={control}
                    defaultValue=""
                    render={( {
                      field,
                      fieldState: { error }
                    } ) => (
                      <div className={"row"}>
                        <label
                          className={"form-label col-lg-4 col-md-4 col-12 p-2"}>PTAN</label>
                        <div className={"col-lg-8 col-md-8 col-12"}>
                          <HubInputField
                            inputId="ptan"
                            isRowBased={false}
                            isRequired={false}
                            field={field}
                            error={error}
                            // inputLabel="PTAN"
                            inputPlaceholder="Provider Transaction Access Number"
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              <button
                type="button" disabled={addProviderLoading}
                className="btn btn-primary mt-2"
                onClick={handleDoctorForm(addNewProvider)}
              >
                {addProviderLoading && (
                  <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                )}
                <span className="d-block d-lg-inline-block ps-2">Add Provider</span>
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  )
}

export default AddProviderModal
